import {
  DocumentDownloadIcon,
  ExclamationIcon,
} from "@heroicons/react/outline";
import classNames from "classnames";
import { DateTime } from "luxon";
import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import toast from "react-hot-toast";
import { Link, useLocation, useParams } from "react-router-dom";

import {
  ApiService,
  Case,
  CaseDocumentAdmin,
  InsurancePlan,
  LISCase,
  LISSlide,
  LISSpecimen,
  Location,
  PatientInsurance,
  SlidePOST,
} from "pwapi";

import { StainList } from "custom-models/frontLoader";

import { useIsLoading, useIsLoadingAny } from "shared/hooks/requestStatusHooks";
import { isSpecimenScannable } from "shared/hooks/useClinicFields";
import { useCanSeeFrontEndAdmin } from "shared/hooks/usePermissions";
import { selectFeatureFlag } from "shared/reducers/sysReducer";
import { selectExternalLabsForLab } from "shared/selectors/externalLabSelectors";
import { displayPriority, getBillingProperties } from "shared/utils/caseUtils";
import {
  getBillingTypeOptions,
  getInitialBillingType,
} from "shared/utils/rulesEngineUtils";
import { procedureEnumToLabel } from "shared/utils/specimenUtils";

import { accessionCase, getCase } from "app/actions/caseActions";
import { scanSpecimen } from "app/actions/specimenActions";
import { setSpecimenBag } from "app/actions/specimenBagActions";
import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  openDocument,
  updateCase,
} from "app/reducers/admin/actions/caseActions";
import { updateInsurance } from "app/reducers/admin/actions/patientActions";
import { createSlide } from "app/reducers/admin/actions/slideActions";
import { useAllowsExternalAccession } from "hooks/useAllowsExternalAccession";
import { useBarcodePrintingCallbacks } from "hooks/useBarcodePrintingCallbacks";
import useBarcodeScanner from "hooks/useBarcodeScanner";
import useInsurance from "hooks/useInsurance";
import {
  useSelectedLab,
  useSelectedLabStation,
} from "hooks/useSelectedLabStation";
import SelectSearchInput from "ui/CaseFormInputs/SelectSearchInput";
import {
  InsurancePlanOption,
  mapInsurancePlansToOptions,
} from "ui/CaseFormInputs/SelectSearchOptions";
import useInsuranceInputs from "ui/CaseFormInputs/useInsuranceInputs";
import InsurancePlanLookupDialog from "ui/CaseForms/InsurancePlanLookupDialog";
import AccessionNumber from "ui/components/AccessionNumber";
import SpecimenProtocolTags from "ui/components/SpecimenProtocolTags";
import Spinner from "ui/components/Spinner";
import { compareCaseToModMed } from "util/caseUtils";
import {
  BarcodePayload,
  modMedFromState,
  ModMedPWPayload,
} from "util/objectFromBarcode";
import { labelForSlide } from "util/slideUtils";
import { slideSort, specimenSort } from "util/sortFunctions";

import PrintSpecimenButton from "./PrintSpecimenButton";

export const Slide = ({
  slide,
  slideCase,
}: {
  slide: LISSlide;
  slideCase: LISCase;
}) => {
  const dispatch = useAppDispatch();
  const stain: StainList | undefined = useAppSelector(
    ({ stains }) => stains[slide.stain_id || 0]
  );
  const block = slideCase.blocks?.find((b) => b.id === slide.block_obj_id);
  const specimen = slideCase.specimens?.find(
    (s) => s.id === block?.specimen_id
  );

  const controlSlide =
    (!slide.is_control &&
      slideCase.slides?.find((s) => {
        const other_block = slideCase.blocks?.find(
          (b) => b.id === s.block_obj_id
        );
        const other_specimen = slideCase.specimens?.find(
          (s) => s.id === other_block?.specimen_id
        );

        return (
          s.stain_id === slide.stain_id &&
          other_specimen?.id === specimen?.id &&
          s.is_control
        );
      })) ||
    undefined;
  const isCreatingSlide = useIsLoading(createSlide);
  const lab = useSelectedLab();
  const lab_station = useSelectedLabStation();

  return (
    <div key={slide.id} className="Case__slides__slide">
      <div>
        <code>
          <strong>{labelForSlide(slide, slideCase, stain)}</strong>
        </code>
        <div>
          <button
            onClick={() => {
              window.open(
                `/print/${slideCase.id}/?slides=${slide.id}&printer=zebra`
              );
            }}
            className="Button Button--lightGrey"
          >
            Print Slide Label
          </button>
        </div>
      </div>
      <p>
        {slide.is_control && (
          <code>
            <strong>CONTROL</strong>
          </code>
        )}{" "}
        {slide.created_at && (
          <>
            {slide.is_rescan
              ? "Rescanned"
              : slide.is_control
              ? "Created"
              : "Cut"}{" "}
            {DateTime.fromISO(slide.created_at).toLocaleString()}
          </>
        )}
        {slide.lab_order_id && <> for Order #{slide.lab_order_id}</>}
      </p>

      {slide.stain_id &&
        !slide.is_control &&
        !slide.is_rescan &&
        !controlSlide &&
        !["ROUTINE", "TECH", "PW-TECH"].includes(stain.category) && (
          <button
            className="Button Button--blue"
            disabled={!!controlSlide || isCreatingSlide}
            onClick={() => {
              slideCase.id &&
                slide.stain_id &&
                specimen?.id &&
                block?.id &&
                dispatch(
                  createSlide({
                    requestBody: {
                      lab_id: lab.id,
                      lab_station_id: lab_station.id,

                      case_id: slideCase.id,
                      // NOTE: accession_prefix_id not an arg for SlidePOST
                      accession_number: slideCase.accession,
                      specimen_id: specimen.id,
                      block_obj_id: block.id,
                      slide_number: slide.slide_number,
                      stain_id: slide.stain_id,

                      // NOTE: Should `lab_order_id` _actually_ be included in the "control slide"?
                      // Time will tell depending on how we handle control slides in a better way
                      // lab_order_id: slide.lab_order_id,

                      status: SlidePOST.status.RECEIVED_LAB,
                      is_control: true,
                    },
                  })
                );
            }}
          >
            Create Control Slide
          </button>
        )}
    </div>
  );
};

const InsurancePlanLocation = ({
  companyName,
  location,
  streetNewLine,
}: {
  companyName: string;
  location?: Location | null;
  streetNewLine?: boolean;
}) => {
  return (
    <>
      {companyName}
      <br />
      {location?.street}
      {location?.street2 && (
        <>
          {streetNewLine ? <br /> : ", "}
          {location.street2}
        </>
      )}
      <br />
      {location?.city}, {location?.state} {location?.zipcode}
    </>
  );
};

const Insurance = ({
  insurance,
  labStationId,
}: {
  insurance: PatientInsurance;
  labStationId?: number;
}) => {
  const dispatch = useAppDispatch();

  const [chosenPlan, setChosenPlan] = useState<InsurancePlan | undefined>(
    undefined
  );
  const [showAdvancedLookup, setShowAdvancedLookup] = useState<boolean>(false);
  const insuranceInputs = useInsuranceInputs({ isAdmin: true });

  const onSubmitPlan = () => {
    insurance?.id &&
      chosenPlan &&
      dispatch(
        updateInsurance({
          id: insurance.id,
          requestBody: {
            plan: { id: chosenPlan.id },
          },
        })
      );
  };

  if (insurance.plan?.id) return null;

  return (
    <div className="section__grid sections__lineItem">
      <h3>Unmatched Insurance</h3>
      <h3>Select Matching Insurance</h3>
      {insurance && (
        <>
          {showAdvancedLookup && (
            <InsurancePlanLookupDialog
              selectedInsurance={chosenPlan}
              apiService={insuranceInputs.insurancePlan.serviceFunction}
              onSelect={(newPlan: InsurancePlan) => {
                setChosenPlan(newPlan);
                setShowAdvancedLookup(false);
              }}
              onClose={() => setShowAdvancedLookup(false)}
            />
          )}
          <div>
            <InsurancePlanLocation
              location={{
                street: insurance.insurance_company_address || "",
                street2: insurance.insurance_company_address2 || undefined,
                city: insurance.insurance_company_city || undefined,
                state: insurance.insurance_company_state || undefined,
                zipcode: insurance.insurance_company_zipcode || "",
              }}
              companyName={insurance.insurance_company_name || ""}
            />
          </div>
          <div className="flex__container flex__container--vertical">
            {!!insurance.plan_best_hits?.length &&
              insurance.plan_best_hits
                .slice(0, 2)
                .map((plan: InsurancePlan) => {
                  return (
                    <label
                      key={`insurance__${insurance.id}_plan__${plan.id}`}
                      className="flex__container"
                    >
                      <input
                        type="radio"
                        value={plan.id}
                        checked={plan.id === chosenPlan?.id}
                        name={`insurance__${insurance.id}`}
                        id={`insurance__${insurance.id}_plan__${plan.id}`}
                        onChange={() => setChosenPlan(plan)}
                      />
                      <span>
                        <InsurancePlanLocation
                          location={plan.payer_location}
                          companyName={plan.payer_name || ""}
                        />
                      </span>
                    </label>
                  );
                })}
            <SelectSearchInput
              id={`manual__select_${insurance.id}`}
              onChange={({ object }: { object: InsurancePlan }) => {
                setChosenPlan(object);
              }}
              filter="searchTerm"
              component={InsurancePlanOption}
              mapToOptions={mapInsurancePlansToOptions}
              apiService={(
                args: Parameters<typeof ApiService.listAdminInsurancePlans>
              ) =>
                ApiService.listLisInsurancePlans({
                  parentLookupLabStationId: labStationId || 0,
                  ...args,
                })
              }
              debounceTime={500}
              inputLimit={3}
            >
              {chosenPlan ? (
                <InsurancePlanOption object={chosenPlan} />
              ) : (
                <>Manually select insurance plan...</>
              )}
            </SelectSearchInput>
          </div>

          <div className="section--full flex__container flex__container--end">
            <button
              type="button"
              className="Button Button--grey"
              onClick={() => setShowAdvancedLookup(true)}
            >
              Advanced Look Up
            </button>
            <button
              onClick={() => onSubmitPlan()}
              className="Button Button--purple"
              disabled={!chosenPlan}
            >
              Confirm Insurance
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const CaseDetail = () => {
  const dispatch = useAppDispatch();
  const canSeeAdmin = useCanSeeFrontEndAdmin();
  const case_id = Number(useParams().case_id);
  const { state: navigationState } = useLocation();
  const selectedLabStationId = useSelectedLabStation().id;
  const lab = useSelectedLab();
  const externalLabs = useAppSelector((state) =>
    selectExternalLabsForLab(state, lab)
  );

  const specimenCase = useAppSelector((state) => state.case);

  const printSpecimenIds = [...(specimenCase?.specimens || [])]
    .sort(specimenSort)
    .map((s) => s.id) as number[];

  const loadingCase = useIsLoading(getCase, {
    id: case_id,
    parentLookupLabStationId: selectedLabStationId,
  });
  const loadingUpdate = useIsLoadingAny(updateCase);

  const lisHistoryLink = useAppSelector((state) =>
    selectFeatureFlag(state, "lisHistoryLink")
  );
  const lisBarcodesLink = useAppSelector((state) =>
    selectFeatureFlag(state, "lisBarcodesLink")
  );
  const sendoutRequisition = useAppSelector((state) =>
    selectFeatureFlag(state, "sendoutRequisition")
  );
  const externalGrossingLab = useAppSelector((state) =>
    selectFeatureFlag(state, "externalGrossingLab")
  );
  const autoPrint = !useAppSelector((state) =>
    selectFeatureFlag(state, "skipAutomaticBarcodePrinting")
  );

  const patient = specimenCase?.patient;

  const { primary: primaryInsurance, additional: additionalInsurance } =
    useInsurance(patient?.insurance);

  const clinic = useAppSelector((state) =>
    patient?.clinic_id ? state.clinics[patient.clinic_id] : undefined
  );

  const specimenBagBarcode = useAppSelector(
    (state) => state.specimenBag?.barcode?.specimen_bag_payload
  );

  const requireExternalAccession = useAllowsExternalAccession({
    clinic: clinic,
    grossed_by_external_lab_id: specimenCase?.grossed_by_external_lab_id,
    accessioning_lab: lab,
    case_type: specimenCase?.type,
    remains_digital_pc: specimenCase?.remains_digital_pc,
  });
  const [externalAccession, setExternalAccession] = useState(
    specimenCase?.external_accession
  );

  const [userMadeBillingTypeSelection, setUserMadeBillingTypeSelection] =
    useState(false);

  const initialBillingType = getInitialBillingType(
    // Remove null as a prospect for typescript
    specimenCase?.billing_type || undefined,
    clinic?.billing_types
  );

  const forceUserToMakeSelection = userMadeBillingTypeSelection
    ? !specimenCase?.billing_type
    : !initialBillingType;

  const disableAccessionButtonDueToBillingType = (() => {
    // A bit nicer to have this functionality in an inline function,
    // ... because it was otherwise a bit difficult to read inline

    // If the case is currently PATCHing on either Priority or Billing Type, disable
    if (loadingUpdate) return true;

    // If the case has no billing type, disable
    if (!specimenCase?.billing_type) return true;

    // If the user is forced to make a selection, disable
    if (forceUserToMakeSelection) return true;

    // If the case has no billing types (is misconfigured),
    // ... it will show all options. So, don't disable.
    if (!clinic?.billing_types?.length) return false;

    // Otherwise, if the case's billing type is set to something other
    // ... that what is available, disable it
    if (!clinic?.billing_types?.includes(specimenCase?.billing_type))
      return true;

    // When in doubt, don't disable
    return false;
  })();

  const disableAccessionButtonDueToInsurancePlan = (() => {
    // If the lab doesn't even use the plan management, don't disable the button
    if (lab.use_manual_insurance) return false;

    // If any insurance comes back which exists, is not manual, and missing an insurance plan, disable the button
    if (
      specimenCase?.patient?.insurance &&
      specimenCase?.patient?.insurance?.find(
        (insur) => insur && !insur.is_manual && !insur.plan
      )
    )
      return true;
  })();

  // Works slightly different from the above functions, this applies per specimen and has to be run on render time
  const exposeManualSpecimenReceiveButton = useCallback(
    (s: LISSpecimen) => {
      // Hide the control if the case is not in ordered status
      if (specimenCase?.status !== LISCase.status.ORDERED) return false;

      // Hide the control if there is no clinic EMR map or if the clinic EMR map indicates that it has a scannable specimen
      if (isSpecimenScannable(clinic)) return false;

      // Show the control if all above checks passed and the is no lis_state or the lis_state is BOTTLED
      if (!s.lis_state || s.lis_state === LISSpecimen.lis_state.BOTTLED)
        return true;

      // If passthrough the final check, it means it is not true and therefore the control should not be displayed
      return false;
    },
    [specimenCase?.status]
  );

  useEffect(() => {
    if (case_id && (!specimenCase?.id || specimenCase.id !== case_id)) {
      dispatch(
        getCase({
          id: case_id,
          parentLookupLabStationId: selectedLabStationId,
        })
      ).catch(() => {
        toast.error(
          "The case you are attempting to view was accessioned at another lab. Please switch to this lab to continue editing this case."
        );
      });
    }
  }, [case_id, specimenCase?.id]);

  const onUpdateCase = useCallback(
    (caseUpdate: { [key: string]: string | boolean }) => {
      if (caseUpdate && specimenCase?.id) {
        dispatch(updateCase({ id: specimenCase.id, ...caseUpdate }));
        if (caseUpdate.billing_type) setUserMadeBillingTypeSelection(true);
      }
    },
    [specimenCase]
  );

  const onSetSpecimenReceived = (specimen: LISSpecimen) => {
    specimen?.id &&
      dispatch(
        scanSpecimen({
          id: specimen.id,
          parentLookupLabStationId: selectedLabStationId,
        })
      );
  };

  const onScanModMedSpecimen = useCallback(
    (mod_med_payload: ModMedPWPayload) => {
      if (specimenCase && compareCaseToModMed(specimenCase, mod_med_payload)) {
        const specimen = specimenCase?.specimens.find(
          (s) => s.name === mod_med_payload?.specimen_name
        );

        if (specimen?.id)
          dispatch(
            scanSpecimen({
              id: specimen.id,
              parentLookupLabStationId: selectedLabStationId,
            })
          );
        // TODO: error display if the above "goes wrong"
      }
    },
    [specimenCase]
  );

  const onScanSpecimen = (specimen_id: number) => {
    dispatch(
      scanSpecimen({
        id: specimen_id,
        parentLookupLabStationId: selectedLabStationId,
      })
    );
  };

  const onScanSpecimenBag = (barcode: BarcodePayload) => {
    dispatch(setSpecimenBag(barcode));
  };

  const onBarcodeScan = (barcode: BarcodePayload) => {
    const { object_type, object_id, mod_med_payload } = barcode;
    if (object_type === "specimen" && mod_med_payload) {
      onScanModMedSpecimen(mod_med_payload);
    } else if (object_type === "specimen") {
      onScanSpecimen(object_id);
    } else if (
      barcode.object_type === "propath-specimen-bag" &&
      barcode.specimen_bag_payload
    ) {
      onScanSpecimenBag(barcode);
    }
  };

  useBarcodeScanner((barcode) => {
    onBarcodeScan(barcode);
  }, lab?.settings?.bag_tracking?.active);

  const lisAltClickScanning = useAppSelector((state) =>
    selectFeatureFlag(state, "lisAltClickScanning")
  );

  const allReceived = specimenCase?.specimens?.every(
    (s) => s.lis_state === "received"
  );

  const { printDocument, printSpecimen } = useBarcodePrintingCallbacks();

  const print = (response: { case: LISCase }) => {
    // Filter out specimens with the DIF Protocol
    let specimenIds = (
      response?.case?.specimens ||
      specimenCase?.specimens ||
      []
    )
      .filter((specimen) => !specimen.protocols?.includes("dif"))
      .sort(specimenSort)
      .map((specimen) => specimen.id);

    if (lab.settings?.print_two_specimen_labels_on_accession?.active) {
      specimenIds = specimenIds.flatMap((id) => [id, id]);
    }

    // Auto-print newly created documents as well
    const documentIds = (
      response?.case?.documents ||
      specimenCase?.documents ||
      []
    )
      .filter((doc) => doc.status !== CaseDocumentAdmin.status.UPLOADED)
      .map((doc) => doc.id);

    if (autoPrint && specimenIds.length > 0) {
      if (lab.settings?.digital_pc?.active) {
        window.open(
          `/print/${specimenCase?.id}/?documents=${documentIds.join(",")}`
        );
      } else {
        window.open(
          `/print/${specimenCase?.id}/?specimens=${specimenIds.join(
            ","
          )}&documents=${documentIds.join(",")}`
        );
      }
    }
  };

  const onAccessionCase = () => {
    if (requireExternalAccession) {
      if (externalAccession) {
        dispatch(
          updateCase({
            id: specimenCase?.id!,
            // @ts-ignore: updateCase doesn't actually take these parameters,
            // it's typed as updateCaseArgs when it should be typed as
            //`updateCaseArgs['requestBody']. external_accession is correct.
            external_accession: externalAccession,
          })
        )
          .then(() => {
            dispatch(
              accessionCase({
                id: specimenCase?.id!,
                requestBody: {
                  // @ts-ignore: TODO: openapi needs to properly reflect parameters for the labstation_id
                  labstation_id: selectedLabStationId,
                  ...(lab?.settings?.bag_tracking?.active && specimenBagBarcode
                    ? { specimen_bag_barcode: specimenBagBarcode }
                    : {}),
                },
              })
            ).then(print);
          })
          .catch((e) => {
            toast.error(e.body);
          });
      } else {
        toast.error("Case Requires an External Accession");
      }
    } else {
      dispatch(
        accessionCase({
          id: specimenCase?.id!,
          requestBody: {
            // @ts-ignore: TODO: openapi needs to properly reflect parameters for the call
            labstation_id: selectedLabStationId,
            ...(lab?.settings?.bag_tracking?.active && specimenBagBarcode
              ? { specimen_bag_barcode: specimenBagBarcode }
              : {}),
          },
        })
      ).then(print);
    }
  };

  if (specimenCase?.id !== case_id || loadingCase)
    return (
      <div className="AppSpinner">
        <Spinner />
      </div>
    );
  return (
    <div className="CaseWrapper">
      <Helmet>
        <title>{`Case ${specimenCase?.id} Details`}</title>
      </Helmet>

      <header className="Case__header">
        <figure className="Logo">
          <Link to={`/cases${navigationState?.search || ""}`}>
            <img src="/logo.svg" alt="Back to case list" />
          </Link>
        </figure>
        {specimenCase?.accession && (
          <AccessionNumber accession={specimenCase?.accession} />
        )}
        <select
          value={specimenCase?.priority || "normal"}
          onChange={(e) => onUpdateCase({ priority: e.target.value })}
        >
          <option value="" disabled>
            Choose priority...
          </option>
          {Object.keys(Case.priority).map((key) => (
            <option key={key} value={key.toLowerCase()}>
              {displayPriority(key)}
            </option>
          ))}
        </select>
        {specimenCase?.patient?.last_name}, {specimenCase?.patient?.first_name}
        <span>
          ({specimenCase?.patient?.sex} &ndash;{" "}
          {specimenCase?.patient?.date_of_birth})
        </span>
        <div>
          {specimenCase?.status === Case.status.ORDERED &&
            lab?.settings?.bag_tracking?.active && (
              <label className="Case__header__input">
                <span>Specimen Bag</span>
                <input
                  name="lis_accession_source__specimen_bag_barcode"
                  type="text"
                  disabled={true}
                  value={specimenBagBarcode || ""}
                  onChange={(e) => setExternalAccession(e.target.value)}
                />
              </label>
            )}
          {specimenCase?.status === Case.status.ORDERED &&
            externalGrossingLab &&
            !!externalLabs?.length && (
              <label className="Case__header__select">
                <span>Gross Externally At</span>
                <select
                  onChange={(e) =>
                    onUpdateCase({ grossed_by_external_lab_id: e.target.value })
                  }
                  value={specimenCase?.grossed_by_external_lab_id}
                >
                  <option key="none" value=""></option>
                  {externalLabs.map((externalLab) => (
                    <option key={externalLab.id} value={externalLab.id}>
                      {externalLab.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
          {specimenCase?.status === Case.status.ORDERED &&
            requireExternalAccession && (
              <label className="Case__header__input">
                <span>External Accession</span>
                <input
                  type="text"
                  required
                  value={
                    externalAccession || specimenCase?.external_accession || ""
                  }
                  onChange={(e) => setExternalAccession(e.target.value)}
                />
              </label>
            )}
          {specimenCase?.status === Case.status.ORDERED && (
            <>
              {lab.settings?.set_eorder_case_type?.active && (
                <label className="Case__header__select">
                  <span>Case Type</span>
                  <select
                    onChange={(e) =>
                      e.target.value && onUpdateCase({ type: e.target.value })
                    }
                    value={specimenCase.type}
                  >
                    {[
                      { label: "", value: Case.type.CLINICAL },
                      {
                        label: "TC-PC Split",
                        value: Case.type.TC_PC,
                      },
                      {
                        label: "Mohs QC",
                        value: Case.type.MOHS_QC,
                      },
                      {
                        label: "Triage",
                        value: Case.type.TRIAGE,
                      },
                      { label: "Test", value: Case.type.TEST },
                    ].map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <label className="Case__header__select">
                <span>Billing Type</span>
                <select
                  onChange={(e) =>
                    e.target.value &&
                    onUpdateCase({ billing_type: e.target.value })
                  }
                  value={
                    userMadeBillingTypeSelection
                      ? specimenCase?.billing_type || ""
                      : initialBillingType
                  }
                >
                  <option value="" disabled>
                    Select billing type...
                  </option>
                  {getBillingTypeOptions(clinic?.billing_types || []).map(
                    (option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    )
                  )}
                </select>
              </label>
              <label className="Case__header__select">
                <span>Self Pay / Indigent</span>
                <select
                  onChange={(e) =>
                    onUpdateCase({ ...getBillingProperties(e.target.value) })
                  }
                  value={
                    specimenCase.indigent
                      ? "indigent"
                      : specimenCase.self_pay_with_attestation
                      ? "self_pay_with_attestation"
                      : specimenCase.self_pay
                      ? "self_pay"
                      : undefined
                  }
                >
                  <option value="">Not Applicable</option>
                  {[
                    {
                      label: "Self Pay",
                      value: "self_pay",
                    },
                    {
                      label: "Self Pay With Attestation",
                      value: "self_pay_with_attestation",
                    },
                    {
                      label: "Indigent",
                      value: "indigent",
                    },
                  ]
                    .filter(
                      (option) =>
                        option.value !== "indigent" ||
                        lab?.settings?.auto_print_indigent_agreement_notice
                          ?.active
                    )
                    .map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                </select>
              </label>
            </>
          )}
          {specimenCase?.status === Case.status.ORDERED && (
            <button
              disabled={
                !allReceived ||
                disableAccessionButtonDueToBillingType ||
                disableAccessionButtonDueToInsurancePlan ||
                !specimenCase.collected_date ||
                (requireExternalAccession ? !externalAccession : false)
              }
              title={[
                ...(!allReceived ? ["Specimens have not been received"] : []),
                ...(disableAccessionButtonDueToBillingType
                  ? ["Please choose a billing type"]
                  : []),
                ...(disableAccessionButtonDueToInsurancePlan
                  ? ["Insurance plan details have not been confirmed"]
                  : []),
              ].join("\n")}
              className="Case__header__accession Button Button--green"
              onClick={() => onAccessionCase()}
            >
              Accession Case
            </button>
          )}
          {lisBarcodesLink && (
            <Link to="barcodes" className="Case__header__edit">
              Barcodes
            </Link>
          )}
          {lisHistoryLink && (
            <Link to="history" className="Case__header__edit">
              History
            </Link>
          )}
          {canSeeAdmin && (
            <Link
              to={`${process.env.REACT_APP_BACKEND_PROTOCOL}://${process.env.REACT_APP_VIEWER_DOMAIN}/admin/cases/${case_id}`}
              className="Case__header__edit"
              target="_blank"
            >
              Edit Case in dot. Admin ↗
            </Link>
          )}
        </div>
      </header>
      {specimenCase?.collected_date ? (
        <aside className="Case__clinic">
          <p>
            Collected{" "}
            <strong>
              {DateTime.fromISO(specimenCase?.collected_date).toLocaleString()}{" "}
            </strong>{" "}
            <span>
              ({DateTime.fromISO(specimenCase?.collected_date).toRelative()})
            </span>{" "}
            {specimenCase?.received_date && (
              <>
                Received{" "}
                <strong>
                  {DateTime.fromISO(
                    specimenCase?.received_date
                  ).toLocaleString()}
                </strong>{" "}
                <span>
                  ({DateTime.fromISO(specimenCase?.received_date).toRelative()})
                </span>
              </>
            )}
          </p>
        </aside>
      ) : (
        <aside className="Case__clinic Case__clinic--missing">
          <p>
            <ExclamationIcon />
            This case is missing a collected date. Please{" "}
            {canSeeAdmin ? (
              <Link
                to={`${process.env.REACT_APP_BACKEND_PROTOCOL}://${process.env.REACT_APP_VIEWER_DOMAIN}/admin/cases/${case_id}`}
                className="Case__header__edit"
                target="_blank"
              >
                Edit Case ↗
              </Link>
            ) : (
              <Link
                to={`/admin/cases/${case_id}`}
                className="Case__header__edit"
              >
                Edit Case
              </Link>
            )}{" "}
            before Accessioning.
          </p>
        </aside>
      )}

      <div className="Case__sections">
        {!lab.use_manual_insurance &&
          specimenCase?.status === Case.status.ORDERED &&
          // Check if there is any insurance which is not a manual insurance and is missing an insurance plan
          !!specimenCase?.patient?.insurance?.find(
            (insur) => !insur.is_manual && !insur.plan
          ) && (
            <section className="InsuranceConfirmation section--full">
              <header>
                <h2>Please Confirm Patient Insurance</h2>
              </header>
              <div className="section__container flex__container flex__container--vertical">
                {primaryInsurance &&
                  !primaryInsurance.is_manual &&
                  !primaryInsurance.plan?.id && (
                    <>
                      <h3>Primary Insurance</h3>
                      <Insurance
                        insurance={primaryInsurance}
                        labStationId={selectedLabStationId}
                      />
                    </>
                  )}
                {!!additionalInsurance?.length &&
                  // Similar to above, only show if there is an insurance which is not manual and is missing an insurance plan.
                  !!additionalInsurance.find(
                    (insur) => !insur.is_manual && !insur.plan
                  ) && (
                    <>
                      <h3>Additional Insurance</h3>
                      {additionalInsurance
                        .filter((insur) => !insur.plan?.id)
                        .map((insur) => (
                          <Insurance
                            insurance={insur}
                            labStationId={selectedLabStationId}
                            key={insur.id}
                          />
                        ))}
                    </>
                  )}
              </div>
            </section>
          )}

        <section>
          <header>
            <h2>Specimen</h2>
            <PrintSpecimenButton
              specimenIds={printSpecimenIds}
              specimenCaseId={specimenCase.id}
            >
              Print All Specimen Labels
            </PrintSpecimenButton>
          </header>
          <div className="section__container flex__container flex__container--vertical">
            {[...specimenCase?.specimens].sort(specimenSort).map((s) => (
              <div key={s.id} className="Specimen sections__lineItem">
                <div>
                  <h2
                    className="Specimen__name Scannable"
                    onClick={(e) => {
                      if (e.altKey && lisAltClickScanning) {
                        onScanModMedSpecimen(modMedFromState(specimenCase, s));
                      }
                    }}
                  >
                    {s.name}
                  </h2>
                  <div className="Specimen__details">
                    <div className="Specimen__status">
                      {s.procedure && (
                        <div className="Specimen__procedure">
                          {procedureEnumToLabel(s.procedure)}
                        </div>
                      )}
                    </div>
                    <div className="Specimen__site">{s.site}</div>
                    <div
                      className={classNames(
                        "Tag",
                        `Tag--${!s.lis_state ? "blue" : "green"}`
                      )}
                    >
                      {s.lis_state || "ordered"}
                    </div>
                    <SpecimenProtocolTags
                      specimen={s}
                      specimens={specimenCase.specimens}
                    />
                  </div>
                  <div className="Specimen__clinicalHistory">
                    <p>{s.clinical_history}</p>
                    <div className="Specimen__actions">
                      <PrintSpecimenButton
                        specimenIds={s.id!}
                        specimenCaseId={specimenCase.id!}
                        variant={"secondary"}
                      >
                        Print Label
                      </PrintSpecimenButton>
                      {exposeManualSpecimenReceiveButton(s) && (
                        <button
                          className="Button Button--purple"
                          onClick={() => {
                            onSetSpecimenReceived(s);
                          }}
                        >
                          Mark Received
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                {s.gross_description && (
                  <>
                    <hr />
                    <div className="Specimen__gross">{s.gross_description}</div>
                  </>
                )}
              </div>
            ))}
          </div>
        </section>

        <section>
          <header>
            <h2>Documents</h2>
          </header>
          <div className="section__container flex__container flex__container--vertical">
            {specimenCase?.documents?.map((d) => (
              <div key={d.id} className="Document sections__lineItem">
                <div>
                  <p>
                    <code>{d.category}</code>
                  </p>
                  <p>
                    {d.status === CaseDocumentAdmin.status.UPLOADED ? (
                      <Link
                        to="#"
                        onClick={(e) => {
                          e.preventDefault();
                          d.id && openDocument(d.id);
                        }}
                      >
                        <code>
                          {d.status} <DocumentDownloadIcon />
                        </code>
                      </Link>
                    ) : (
                      <code>{d.status}</code>
                    )}
                  </p>
                  {d.category !== CaseDocumentAdmin.category.CLINIC_NOTES && (
                    <button
                      className="Button Document__print"
                      disabled={!specimenCase?.accession}
                      onClick={() =>
                        printDocument(d.id as number, specimenCase.id as number)
                      }
                    >
                      Print Label
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          {sendoutRequisition && (
            <div className="Case__sendouts">
              <Link
                to={`/cases/${specimenCase.id}/sendout_requisition`}
                className="Button Button--purple"
                target="_blank"
              >
                Generate Sendout Requisition
              </Link>
            </div>
          )}
        </section>

        {!!specimenCase?.slides?.length && (
          <section className="section--full">
            <header>
              <h2>Slides</h2>
            </header>
            <div className="Case__slides">
              {[...specimenCase?.slides].sort(slideSort).map((s) => (
                <Slide key={s.id} slide={s} slideCase={specimenCase} />
              ))}
            </div>
          </section>
        )}
      </div>

      {/* {lastScan?.payload?.mod_med_payload && (
        <div className="ModMedScanWrapper">
          <aside className="ModMedScan">
            <div>
              <h2 className="ModMedScan__specimen">{modmed?.specimen_name}</h2>
            </div>

            <div>
              <label>
                Patient Name{" "}
                {modmed?.last_name === patient?.last_name &&
                modmed?.first_name === patient?.first_name ? (
                  <CheckCircleIcon />
                ) : (
                  <XCircleIcon />
                )}
              </label>
              <p>
                {modmed?.last_name}, {modmed?.first_name}
              </p>
            </div>

            <div>
              <label>
                DOB{" "}
                {modmed?.date_of_birth === patient?.date_of_birth ? (
                  <CheckCircleIcon />
                ) : (
                  <XCircleIcon />
                )}
              </label>
              <p>{modmed?.date_of_birth}</p>
            </div>

            <div>
              <label>
                MRN{" "}
                {modmed?.medical_record_number ===
                patient?.medical_record_number ? (
                  <CheckCircleIcon />
                ) : (
                  <XCircleIcon />
                )}
              </label>

              {modmed?.medical_record_number}
            </div>

            <div
              className={classNames({
                ModMedScan__off:
                  modmed?.external_patient_id !== patient?.external_patient_id,
              })}
            >
              <label>
                External ID{" "}
                {modmed?.external_patient_id ===
                patient?.external_patient_id ? (
                  <CheckCircleIcon />
                ) : (
                  <XCircleIcon />
                )}
              </label>

              {modmed?.external_patient_id}
            </div>

            {allReceived && specimenCase?.status === Case.status.ORDERED && (
              <button
                disabled={!allReceived}
                className="Case__header__accession Button Button--green"
                onClick={() => {
                  specimenCase?.id &&
                    dispatch(accessionCase(specimenCase?.id, navigate));
                }}
              >
                Accession Case
              </button>
            )}
          </aside>
        </div>
      )} */}
    </div>
  );
};

export default CaseDetail;
